/**
 * ユーザー専用機能のMixpanel分析設定
 *
 * このファイルにはユーザー専用機能のフォーム設定を定義します：
 */

import type {
  TrackedButtonMeta,
  TrackedFormMeta,
} from '@/constants/mixpanel/types'

import { Feature, Action, EventName } from '@/constants/mixpanel/constants'

/**
 * ユーザー機能のフォーム分析設定
 */
export const UserForms = {
  // ユーザーの取引企業関連（ユーザーが管理）
  PartnerCompany: {
    Upsert: {
      feature: Feature.PartnerCompany,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'submit_partner_company_upsert',
    } as TrackedFormMeta,
  },
  // 見積もりリクエスト関連
  QuoteRequest: {
    AirTransport: {
      feature: Feature.QuoteRequest,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'air_transport_quote_request',
    } as TrackedFormMeta,
    OceanTransport: {
      feature: Feature.QuoteRequest,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'ocean_transport_quote_request',
    } as TrackedFormMeta,
    AirBooking: {
      feature: Feature.Quote,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'air_booking_with_quote',
    } as TrackedFormMeta,
    OceanBooking: {
      feature: Feature.Quote,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'ocean_booking_with_quote',
    } as TrackedFormMeta,
  },
  // 予約・ブッキング関連
  Booking: {
    Air: {
      feature: Feature.Booking,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'air_create_booking',
    } as TrackedFormMeta,
    Ocean: {
      feature: Feature.Booking,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'ocean_create_booking',
    } as TrackedFormMeta,
  },
  // アカウント関連
  Account: {
    Registration: {
      feature: Feature.Account,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'user_registration',
    } as TrackedFormMeta,
  },
  // 認証関連
  Auth: {
    Login: {
      feature: Feature.Auth,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'login',
    } as TrackedFormMeta,
    SwitchAccountLogin: {
      feature: Feature.Auth,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'switch_account_login',
    } as TrackedFormMeta,
  },
  // パスワード関連
  Password: {
    Reset: {
      feature: Feature.Password,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'password_reset',
    } as TrackedFormMeta,
    Update: {
      feature: Feature.Password,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'password_update',
    } as TrackedFormMeta,
  },
  // ゲスト管理
  Guest: {
    CreateUpdateGroup: {
      feature: Feature.Guest,
      action: Action.Upsert,
      event: EventName.FormSubmitted,
      form_type: 'guest_group_create_update',
    } as TrackedFormMeta,
    InviteUser: {
      feature: Feature.Guest,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'guest_user_invite',
    } as TrackedFormMeta,
    Registration: {
      feature: Feature.Guest,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'guest_registration',
    } as TrackedFormMeta,
  },
  // 船積み関連
  Shipment: {
    Customs: {
      feature: Feature.Shipment,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'shipment_customs',
    } as TrackedFormMeta,
    DestinationDelivery: {
      feature: Feature.Shipment,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'shipment_destination_delivery',
    } as TrackedFormMeta,
    ManagementNumber: {
      feature: Feature.Shipment,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'shipment_management_number',
    } as TrackedFormMeta,
  },
  // 事前登録
  PreRegistration: {
    Create: {
      feature: Feature.PreRegistration,
      action: Action.Create,
      event: EventName.FormSubmitted,
      form_type: 'pre_registration',
    } as TrackedFormMeta,
  },
  // 配送希望日
  DeliveryDate: {
    Edit: {
      feature: Feature.Shipment,
      action: Action.Update,
      event: EventName.FormSubmitted,
      form_type: 'desired_delivery_date_edit',
    } as TrackedFormMeta,
  },
} as const

/**
 * ユーザー機能のボタン分析設定
 */
export const UserButtons = {
  Quotes: {
    View: {
      feature: Feature.Quote,
      button_name: 'view_quotes',
      action: Action.Click,
    } as TrackedButtonMeta,
  },
  QuoteRequest: {
    Request: {
      feature: Feature.QuoteRequest,
      button_name: 'new_quote_request',
      action: Action.Click,
    } as TrackedButtonMeta,
  },
  Booking: {
    Request: {
      feature: Feature.Booking,
      button_name: 'new_booking',
      action: Action.Click,
    } as TrackedButtonMeta,
    RequestWithQuote: {
      feature: Feature.Booking,
      button_name: 'new_booking_with_quote',
      action: Action.Click,
    } as TrackedButtonMeta,
    SelectSchedule: {
      feature: Feature.Booking,
      button_name: 'select_schedule',
      action: Action.Click,
    } as TrackedButtonMeta,
    InputDetail: {
      feature: Feature.Booking,
      button_name: 'input_detail',
      action: Action.Click,
    } as TrackedButtonMeta,
    Confirm: {
      feature: Feature.Booking,
      button_name: 'confirm',
      action: Action.Click,
    } as TrackedButtonMeta,
  },
  Shipment: {
    DeliveryRequestSuggestion: {
      feature: Feature.Shipment,
      button_name: 'delivery_request_suggestion',
      action: Action.Click,
    } as TrackedButtonMeta,
  },
} as const
